/**
 * D-020 LT2-003 — the two-sided expanded ladder for a derivative line.
 *
 * Replaces the single-side `LadderRowsFor` in the compact spread/total
 * expanded slot when the trader has lt2Access: BOTH executable sides render
 * as adjacent labeled columns — YES/OVER on the LEFT, NO/UNDER on the RIGHT
 * (this left/right order is also the A/D hotkey contract), each with
 * mirrored asks over bids from the shared `buildSideLevels` math, own
 * resting-order chips, and click-to-trade levels. The trader can alternate
 * sides without any toggle or panel transition.
 *
 * Own-order injection (LT2-009): a confirmed resting order whose price has no
 * book level yet — a just-accepted new top of book, before the feed ticks —
 * renders as a dashed injected level showing ONLY the trader's own size, so
 * an accepted order is never invisible. Injection never fabricates market
 * depth: the size shown is the order's own remaining count, marked as yours.
 *
 * Presentation (#882, V4-10 / V4-02b): `presentation` selects depth and
 * layout. The `default` layout is the original two half-width columns at the
 * shared three levels with the original buy-only injection — the Value
 * Dashboard's SpreadsTotals page and the nearest-three Butterfly panel keep
 * it untouched, DOM for DOM. The `compact` layout is the Game Detail
 * derivative book: five levels per block, both columns packed toward the
 * shared center, each rung reading price | odds, resting qty and own-order
 * chips with nothing stretched between them, and a permanent one-row spread
 * rung between the asks and the bids that reads the spread or holds one
 * accepted order resting inside it. Own-only rows never enter the ask block
 * (its extras are listed from the permanent column header), the spread rung
 * is exactly one row, and the bid block is the market rows followed by a
 * visibly separate own block (up to `MAX_EXTRA_OWN_ROWS` own-only rows —
 * feed-gap prices and prices beyond the displayed depth alike — best first,
 * then the disclosure row). Own-order state therefore never moves a market
 * row, the spread rung or the best prices; the ladder grows by at most four
 * rows at its bottom edge. A feed level arriving or leaving still moves
 * rows, as in any levels ladder. Visual left/right is display only; every click
 * and hotkey still resolves the column's own side, exactly as before.
 *
 * Compact own-order truth (#882): an own order resting at a price the feed
 * DOES carry, but deeper than the displayed depth, is a different fact from
 * an injected one. It renders with the level's real market size and its own
 * title, never as "awaiting book confirmation" — the book has confirmed it;
 * the display simply cuts off above it. Sells get the same treatment on the
 * ask block as buys on the bid block. Own-only rows are BOUNDED per block
 * (`MAX_EXTRA_OWN_ROWS` inline, the rest in one summary row) so a trader
 * with orders at many deep prices cannot grow the book without limit. The
 * classification needs the whole sanity-filtered side, which is resolved
 * lazily and only when an own order is actually missing from the displayed
 * levels — a book with no own orders does exactly the work it did before.
 *
 * Clicks are inert unless the line's availability is 'active' — the same
 * classifier the pills use, so a suspended/stale line can't accept an order
 * through its ladder either.
 */
import { ReferenceRail, type LadderReference } from './ReferenceFloater';
import { OrderbookFreshness } from '@/components/atoms/OrderbookFreshness';
import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/infra/utils';
import { Td } from '@/components/atoms/TableCells';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { fromCentiCents, toCentiCents } from '@/lib/pricing/subCent';
import type { BookLevel, VenueBook } from '../ValueDashboard/types';
import type { UserOrderSlice } from '@/hooks/useUserKalshiPOS';
import type { LineAvailability } from '@/lib/sport/lineAvailability';
import { americanOdds } from '../ValueDashboard/tradeModalBook';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useFeeDisplayStatus, useFeeFor } from '@/services/hydraFees';
import {
  FEE_DISPLAY_MESSAGE,
  FeeScheduleMarker,
  feeDisplayCanHide,
} from '@/components/molecules/FeeScheduleMarker';
import {
  LADDER_LEVELS,
  buildSideLevels,
  formatVolume,
  ladderRowPricePresentation,
  queueDepth,
  orderTooltip,
  type BidRowOrder,
} from '../ValueDashboard/ladder/helpers';
import { RestingOrderGroup } from '../ValueDashboard/ladder/RestingOrderGroup';
import { CancelLevelControl } from '../ValueDashboard/ladder/CancelLevelControl';

export type DerivativeLadderLayout = 'default' | 'compact' | 'narrow';

/** How a two-sided derivative book is drawn: how many market levels per
 *  block (asks and bids, per side) and which geometry. */
export interface DerivativeLadderPresentation {
  depth: number;
  layout: DerivativeLadderLayout;
}

/** The original ladder: shared three levels, two half-width columns. Every
 *  consumer that does not name a presentation gets exactly this. */
export const DEFAULT_LADDER_PRESENTATION: DerivativeLadderPresentation = {
  depth: LADDER_LEVELS,
  layout: 'default',
};

/**
 * #882 — the Game Detail non-Butterfly derivative book: five bids and five
 * asks per side (the trader's "good start"; eight is an excluded optional),
 * packed compact geometry. Deliberately a separate constant from
 * `LADDER_LEVELS`, which still governs every other ladder in the app.
 */
export const GAME_DETAIL_COMPACT_LADDER: DerivativeLadderPresentation = {
  depth: 5,
  layout: 'compact',
};

/**
 * #884 — the Butterfly card's book: five levels in the compact
 * geometry (spread rung, header-listed asks, separate own block) drawn
 * NARROW, so three cards sit beside the 384px rail at 1920 and 2560. The
 * own-order cell carries a direct level cancel plus the count disclosure
 * (`+N`, no inline chip) — an
 * explicit presentation override, so the trader's #677 expanded-chip
 * preference cannot allocate the expanded region's fixed height inside a
 * rung and move the prices below it: the existing popover behind the count
 * carries every chip with its remaining size, queue reading and cancel, so
 * no order fact is lost — it is one click from the rung instead of on it.
 * Rung text matches the compact book. Five levels are the September 16
 * client clarification; the shared dashboard default remains three.
 */
export const BUTTERFLY_NARROW_LADDER: DerivativeLadderPresentation = {
  depth: 5,
  layout: 'narrow',
};

/** Reserve direct cancellation and the details count before orders arrive. */
const NARROW_OWN_CELL = 'w-[46px] min-w-[46px] max-w-[46px]';
/**
 * Every compact/narrow table row — market rung, spread rung, blank pad, own
 * block row, disclosure row — is exactly this tall, borders included: the
 * cells carry transparent top/bottom borders that marked rows only recolor.
 * Equal rows in both columns is what keeps the two spread rungs level and
 * keeps an own order's arrival from moving any price (#884 measured a 4px
 * shift from a taller own spread rung, and fractional shifts from the other
 * column stretching to match — see `items-start` on the container).
 */
const COMPACT_ROW = 'h-[22px]';
/** Compact own-order cell: the bounded chip group's widest form (168px:
 *  #650 compact `+N`, #677 expanded max-width) plus the level cancel that
 *  leads it and its gap (16px, #1190) — measured, so the cancel takes no
 *  width from the chips beside it. */
const COMPACT_OWN_CELL = 'w-[184px] min-w-[184px] max-w-[184px]';

/**
 * Compact layout: own-only rows (injected or beyond-depth) the BID block
 * draws inline below its market rows before the rest go to its disclosure
 * row. Three keeps the ladder's downward growth at four rows; the disclosure
 * still names every price, size and order count, so no accepted order
 * becomes invisible. The ask block draws none inline (see `inlineExtras`).
 */
export const MAX_EXTRA_OWN_ROWS = 3;

export const INJECTED_LEVEL_TITLE =
  'Your order — accepted and resting; awaiting book confirmation';
export const INJECTED_SIZE_TITLE =
  'Your own resting contracts (book has not shown this level yet)';
export const RESTING_SIZE_TITLE = 'Resting Qty — contracts resting at this price level';
export const INACTIVE_LEVEL_TITLE = 'Order entry disabled — market not active';

export interface TwoSidedLadderProps {
  kalshi: VenueBook | null;
  venue?: 'kalshi' | 'polymarket' | 'polymarket_us';
  reference?: LadderReference;
  /** Column headers — "YES"/"NO" for spreads, "OVER"/"UNDER" for totals. */
  yesLabel: string;
  noLabel: string;
  /** The trader's open orders on this line's ticker (mergeOrdersForTicker
   *  output); null when there is no Kalshi book. */
  userOrders: UserOrderSlice[] | null;
  availability: LineAvailability;
  /** Maker-fee rebate fraction (0..1) folded into bid-row odds. */
  makerRebatePct: number;
  /** Level click → the parent maps to onPillTrade with
   *  {origin:'ladder', kind} so maker semantics survive. */
  onLevelClick: (side: 'yes' | 'no', kind: 'bid' | 'ask', cents: number) => void;
  /** Host table column count for the wrapper cell. */
  colSpan?: number;
  /** D-020 V2B — paints the hotkey-focus treatment on the expanded slot so
   *  the focused card reads as one visual unit with its action row. */
  focused?: boolean;
  /** #609 — the focused card's id, so the expanded slot can carry the hotkey
   *  fence's marks. This row is where the submittable price is DISPLAYED once
   *  the card is expanded (the resolver reads the join bid from here, not from
   *  the action row's pills), so the fence tests THIS row's visibility. Null
   *  whenever hotkeys are off — the marks must never appear then. */
  lt2FocusId?: string | null;
  /** D-020 LT2-018 — render the NO column LEFT (team-side toggle display
   *  order). Each column's side/labels/clicks are untouched. */
  flipColumns?: boolean;
  /** #882 — depth and geometry. Omitted = `DEFAULT_LADDER_PRESENTATION`. */
  presentation?: DerivativeLadderPresentation;
}

export interface LevelRow {
  level: BookLevel;
  /** True when this level exists only because the trader's own confirmed
   *  resting order sits there and the book hasn't shown it yet (LT2-009).
   *  `level.size` is the trader's OWN remaining count, not market depth. */
  injected: boolean;
  /** True when the feed carries this level but it sits beyond the displayed
   *  depth; it is drawn only because the trader's own order rests there.
   *  `level.size` is the REAL market size at that price. */
  beyondDepth: boolean;
}

/** Where an own-only price sits relative to the displayed book. */
export type OwnPlacement = 'beyond-depth' | 'not-in-book' | 'inside-spread';

/** One of the trader's orders as the disclosure lists it: what is left and
 *  how much sits ahead of it, the same two facts its chip would carry. */
export interface OwnOverflowOrder {
  orderId: string;
  remainingCount: number;
  /** Contracts queued ahead at this price and better, when the book can
   *  say; null when the venue has not reported a queue position. */
  queueOrBetter: number | null;
  /** #666 — a remembered (last confirmed) queue reading, not a live one. */
  queueRetained?: boolean;
}

/** One own-only price that did not earn an inline row. */
export interface OwnOverflow {
  priceCents: number;
  /** The trader's own remaining contracts at that price, summed. */
  ownSize: number;
  /** How many of the trader's orders rest at that price. A price is not an
   *  order; the disclosure counts both. */
  orderCount: number;
  placement: OwnPlacement;
  /** #884 — every order at the price with its remaining size and queue
   *  reading, so nothing a chip would show is lost by not drawing the row. */
  orders: OwnOverflowOrder[];
}

/** An own-only level with the trader's aggregate size and order count. */
export interface OwnExtra extends LevelRow {
  ownSize: number;
  orderCount: number;
  /** The orders behind the aggregate, for the disclosure's per-order facts. */
  orders: UserOrderSlice[];
}

export function ownExtraToOverflow(
  e: OwnExtra,
  placement?: OwnPlacement,
  book: VenueBook | null = null,
): OwnOverflow {
  return {
    priceCents: e.level.priceCents,
    ownSize: e.ownSize,
    orderCount: e.orderCount,
    placement: placement ?? (e.beyondDepth ? 'beyond-depth' : 'not-in-book'),
    orders: e.orders.map((o) => ({
      orderId: o.orderId,
      remainingCount: o.remainingCount,
      // Same reading the chip shows (`queueDepth(...).atLevelOrBetter`);
      // without a book the venue's own position is the most it can say.
      queueOrBetter: book ? queueDepth(o, book).atLevelOrBetter : (o.queuePosition ?? null),
      queueRetained: o.queueRetained,
    })),
  };
}

export interface OwnLevelsResult {
  rows: LevelRow[];
  overflow: OwnOverflow[];
  /** Injected rows better than the block's best displayed level — accepted
   *  orders resting INSIDE the spread — best first. Empty unless `spreadSlot`. */
  spread: OwnExtra[];
}

export interface WithOwnLevelsOptions {
  /**
   * The whole sanity-filtered side, resolved lazily and only when an own
   * order is absent from `displayed`. When omitted every absent own price is
   * injected — the original LT2-009 behavior the default layout keeps.
   */
  resolveFull?: () => BookLevel[];
  /** Which block this is, for the proximity order of beyond-depth rows:
   *  bids sit below the block (nearest = highest price), asks above it
   *  (nearest = lowest price). */
  kind: 'bid' | 'ask';
  /** Inline cap for own-only rows; omitted = unbounded (default layout). */
  maxExtra?: number;
  /**
   * Compact layout: return injected rows that are better than the block's
   * best displayed level in `spread` instead of `rows`. They rest inside the
   * spread, and the compact column draws the best of them in its permanent
   * spread rung, so an acceptance never inserts a row into the market block.
   * Omitted (the default layout) keeps them interleaved by price as LT2-009
   * always did.
   */
  spreadSlot?: boolean;
  /**
   * `false` draws NO own-only row inline: every block extra goes to
   * `overflow`. The compact ask block uses this, because rows added above the
   * asks would push the whole book down under the trader's pointer; its
   * extras are listed from the column header instead. Default `true`.
   */
  inlineExtras?: boolean;
  /**
   * Compact bid block: keep the displayed market rows first, in their own
   * order, and append the inline own-only rows after them as a separate
   * block (best first) instead of interleaving them by price. An accepted
   * buy at a feed-gap price between two displayed bids then never shifts
   * the market rows under the pointer. Omitted (the default layout) keeps
   * the original interleave.
   */
  separateOwnBlock?: boolean;
  /** The line's book, so listed orders can carry the same queue reading
   *  their chips would (`queueDepth`). Omitted: the venue's own position. */
  book?: VenueBook | null;
}

/**
 * Merge the trader's own resting levels that are absent from the DISPLAYED
 * levels, sorted best-first for bids and worst-first for asks (both descend
 * by price, which is the visual order each block reads in).
 *
 *   • A price the feed carries beyond `displayed` → the feed's level, marked
 *     `beyondDepth` — real market size, the trader's chips ride it.
 *   • A price the feed has no level for → an injected level whose size is the
 *     sum of the trader's own remaining counts there. Never fabricated depth.
 *
 * Orders with nothing remaining add no row. Injected rows take the inline
 * slots first (an accepted order the feed has not shown is the one state
 * that must never hide), then beyond-depth rows nearest the block; the rest
 * are returned as `overflow` for a bounded summary.
 */
export function withOwnLevels(
  displayed: BookLevel[],
  own: UserOrderSlice[],
  opts: WithOwnLevelsOptions,
): OwnLevelsResult {
  // Both blocks read top-down in DESCENDING price: bids best-first, asks
  // worst-first with the best ask nearest the separator.
  const byPriceDesc = (a: LevelRow, b: LevelRow) => b.level.priceCents - a.level.priceCents;
  const rows: LevelRow[] = displayed.map((level) => ({
    level,
    injected: false,
    beyondDepth: false,
  }));
  const displayedPrices = new Set(displayed.map((l) => l.priceCents));
  const ownByPrice = new Map<number, number>();
  const ordersByPrice = new Map<number, UserOrderSlice[]>();
  for (const o of own) {
    if (o.remainingCount <= 0) continue;
    if (displayedPrices.has(o.priceCents)) continue;
    ownByPrice.set(o.priceCents, (ownByPrice.get(o.priceCents) ?? 0) + o.remainingCount);
    const atPrice = ordersByPrice.get(o.priceCents);
    if (atPrice) atPrice.push(o);
    else ordersByPrice.set(o.priceCents, [o]);
  }
  if (ownByPrice.size === 0) return { rows: rows.sort(byPriceDesc), overflow: [], spread: [] };

  // Only now does the whole side get walked: a column with nothing to
  // classify never pays for it.
  const feedByPrice = new Map(
    (opts.resolveFull?.() ?? []).map((l) => [l.priceCents, l] as const),
  );
  const extras: OwnExtra[] = [];
  for (const [priceCents, ownSize] of ownByPrice) {
    const feedLevel = feedByPrice.get(priceCents);
    const orders = ordersByPrice.get(priceCents) ?? [];
    const orderCount = orders.length;
    extras.push(
      feedLevel
        ? { level: feedLevel, injected: false, beyondDepth: true, ownSize, orderCount, orders }
        : {
            level: { priceCents, size: ownSize },
            injected: true,
            beyondDepth: false,
            ownSize,
            orderCount,
            orders,
          },
    );
  }
  // Inside the spread: better than the block's best displayed level (or the
  // block is empty). Only injected rows can be — a feed level better than the
  // displayed best would itself be displayed. With `spreadSlot` those rows
  // leave the block entirely and are returned best first.
  const best = displayed.length
    ? opts.kind === 'bid'
      ? Math.max(...displayed.map((l) => l.priceCents))
      : Math.min(...displayed.map((l) => l.priceCents))
    : null;
  const insideSpread = (e: LevelRow) =>
    e.injected &&
    (best == null || (opts.kind === 'bid' ? e.level.priceCents > best : e.level.priceCents < best));
  const spread: OwnExtra[] = [];
  const blockExtras: OwnExtra[] = [];
  for (const e of extras) (opts.spreadSlot && insideSpread(e) ? spread : blockExtras).push(e);
  spread.sort((a, b) =>
    opts.kind === 'bid'
      ? b.level.priceCents - a.level.priceCents
      : a.level.priceCents - b.level.priceCents,
  );
  // Block extras: injected first; then beyond-depth by proximity to the
  // displayed block.
  const nearer = (a: LevelRow, b: LevelRow) =>
    opts.kind === 'bid'
      ? b.level.priceCents - a.level.priceCents
      : a.level.priceCents - b.level.priceCents;
  blockExtras.sort((a, b) => {
    if (a.injected !== b.injected) return a.injected ? -1 : 1;
    return nearer(a, b);
  });
  const cap = opts.inlineExtras === false ? 0 : (opts.maxExtra ?? Number.POSITIVE_INFINITY);
  const inline = blockExtras.slice(0, cap);
  const overflow = blockExtras.slice(cap).map((e) => ownExtraToOverflow(e, undefined, opts.book ?? null));
  const inlineRows: LevelRow[] = inline.map((e) => ({
    level: e.level,
    injected: e.injected,
    beyondDepth: e.beyondDepth,
  }));
  if (opts.separateOwnBlock) {
    // Market rows keep their order and their positions; the own block
    // follows them as its own small ladder, best first.
    rows.sort(byPriceDesc);
    rows.push(...inlineRows.sort(byPriceDesc));
  } else {
    rows.push(...inlineRows);
    rows.sort(byPriceDesc);
  }
  overflow.sort((a, b) => b.priceCents - a.priceCents);
  return { rows, overflow, spread };
}

interface ColumnData {
  side: 'yes' | 'no';
  label: string;
  /** Worst ask first, best ask last (nearest the separator). */
  askRows: LevelRow[];
  /** Best bid first. */
  bidRows: LevelRow[];
  /** Compact only: own-only asks that are not drawn — every own ask extra,
   *  listed from the column header so the ask block never grows upward. */
  askOverflow: OwnOverflow[];
  /** Compact only: own-only bids past the inline cap, listed from the row
   *  below the bid block. */
  bidOverflow: OwnOverflow[];
  /** Compact only: the ONE own order the spread rung draws — an ask inside
   *  the spread first, else a bid, best first. Any other inside-spread order
   *  is listed by its block's disclosure. */
  spreadRow: { row: LevelRow; kind: 'bid' | 'ask' } | null;
  /** Compact only: best displayed ask minus best displayed bid in this side's
   *  cents, or null when either block is empty. */
  spreadCents: number | null;
  ownBuys: UserOrderSlice[];
  ownSells: UserOrderSlice[];
}

/** Rows a block occupies including its summary row, for padding. */
function blockRows(rows: LevelRow[], overflow: OwnOverflow[]): number {
  return rows.length + (overflow.length > 0 ? 1 : 0);
}

interface LevelPresentation {
  cents: number;
  priceText: string;
  odds: string | null;
  priceTone: string;
  chips: BidRowOrder[];
  clickable: boolean;
  title: string | undefined;
  sizeTitle: string;
  onClick: ((e: React.MouseEvent) => void) | undefined;
}

/**
 * The compact column's spread rung: the permanent one-row band between the
 * asks and the bids. Empty, it reads the spread in this side's cents (or `—`
 * when a block is empty); when the trader's accepted order rests inside the
 * spread the rung holds that order instead (see `withOwnLevels` `spreadSlot`).
 * Because the slot always exists, an acceptance and its later disappearance
 * move no market row.
 */
function CompactSpreadRung({
  text,
  position,
}: {
  text: string | null;
  position: 'left' | 'right';
}) {
  return (
    <tr aria-hidden={text == null ? true : undefined} data-ladder-spread="" className={COMPACT_ROW}>
      <td
        colSpan={3}
        className={cn(
          'border-border/40 border-y px-1.5 py-0 align-middle',
          position === 'left' ? 'text-right' : 'text-left',
        )}
      >
        <span className="text-muted-foreground/60 block h-4 whitespace-nowrap text-[10px] leading-4">
          {text ?? ''}
        </span>
      </td>
    </tr>
  );
}

/** A blank rung the compact layout pads a block with. Same height as a real
 *  rung so the two columns' separators stay aligned and the block's height
 *  never depends on how many levels the feed happens to carry. */
function CompactBlankRung({ state, mark }: { state: string | null; mark?: Lt2CellMark }) {
  return (
    <tr aria-hidden={state == null ? true : undefined} data-ladder-blank="" className={COMPACT_ROW}>
      <td colSpan={3} className="border-y border-transparent px-1.5 py-0 align-middle">
        <span className="text-muted-foreground/50 block h-4 whitespace-nowrap text-[10px] leading-4">
          {/* #953 — an exact mark sits on the WORDS, not the full-width block,
              so a partly cut column still reads "No displayed price" while
              the words themselves show. */}
          {mark ? <span {...mark}>{state ?? ''}</span> : (state ?? '')}
        </span>
      </td>
    </tr>
  );
}

/** Fixed panel geometry so it can be placed from the trigger's rect without
 *  measuring, the way the resting-order popover does. */
// Wide enough that the widest cell of each column (`12345 resting`,
// `12 orders`, `inside spread`) sits on one line at the panel's 10px mono.
const OVERFLOW_PANEL_WIDTH = 320;
const OVERFLOW_PANEL_MAX_HEIGHT = 168;

const PLACEMENT_LABEL: Record<OwnPlacement, string> = {
  'beyond-depth': 'beyond view',
  'not-in-book': 'not in book',
  'inside-spread': 'inside spread',
};

/**
 * The bounded disclosure of own-only prices that are not drawn.
 *
 * A real `<button>` carrying the counts (prices, orders, resting size) that
 * opens a non-modal portal panel listing every such price with its resting
 * size, order count and placement. The panel is informational: no level, no
 * cancel, nothing dispatches, and it is portalled to `<body>` so opening it
 * never changes the ladder's geometry. Two hosts: the row below the bid
 * block (`CompactOverflowRow`), which only ever appends below the bids, and
 * the column header for the ask side (`inline`), which is a permanent row —
 * so neither host ever inserts a row above a market level.
 *
 * Hotkeys: the trigger is a real `<button>` inside the focused row, which
 * `Lt2HotkeyListener` marks as its hotkey-control region and where bare
 * buttons would hand A/D back to the aimed card. This control is not one of
 * LT2's arm/line/perspective/price controls, so it carries `data-lt2-owner`
 * — the same opt-out #602 gave the fee-explanation button that sits inside
 * the boundary: a request to READ something must never be an A/D launch
 * site. While the trigger holds focus A/D are refused (the listener's
 * refusal ack shows why); the aim never moves (its clicks stop before the
 * card's focus-region handler); nothing from the list ever becomes a target.
 * The panel is `aria-modal="false"` so it does not suppress the hotkeys
 * page-wide the way a modal dialog would. Escape closes and returns focus to
 * the trigger; an outside pointer closes without moving focus.
 */
function OverflowDisclosure({
  overflow,
  label,
  kind,
  position,
  inline = false,
  narrow = false,
}: {
  overflow: OwnOverflow[];
  label: string;
  kind: 'bid' | 'ask';
  /** Which side of the center the column sits on; the summary text reads
   *  toward the center like the column's own rungs. */
  position: 'left' | 'right';
  /** Header form: an inline pill after the column label instead of a
   *  full-width row line. */
  inline?: boolean;
  /** #884 narrow column: the header has room for the order count and the
   *  caret only; the price and size counts stay in the panel heading and
   *  the accessible name. */
  narrow?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useDismissibleLayer({
    open,
    onDismiss: (reason) => {
      setOpen(false);
      if (reason === 'escape') triggerRef.current?.focus();
    },
    refs: [triggerRef, panelRef],
  });
  const prices = overflow.length;
  const orders = overflow.reduce((n, o) => n + o.orderCount, 0);
  const resting = Math.round(overflow.reduce((n, o) => n + o.ownSize, 0));
  const summary =
    `${prices} ${prices === 1 ? 'price' : 'prices'} · ` +
    `${orders} ${orders === 1 ? 'order' : 'orders'} · ${resting} resting`;
  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0;
      const viewportW = typeof window !== 'undefined' ? window.innerWidth : 0;
      const below = rect.bottom + 4;
      const top =
        viewportH > 0 && below + OVERFLOW_PANEL_MAX_HEIGHT > viewportH
          ? Math.max(8, rect.top - 4 - OVERFLOW_PANEL_MAX_HEIGHT)
          : below;
      const left =
        viewportW > 0 ? Math.min(rect.left, viewportW - OVERFLOW_PANEL_WIDTH - 8) : rect.left;
      setPos({ top, left: Math.max(8, left) });
    }
    setOpen(true);
  };
  const tone = kind === 'bid' ? 'text-positive' : 'text-negative';
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // Opts out of the row's A/D hand-back: see the header. Same marker
        // and same reason as the fee-explanation button (#602).
        data-lt2-owner=""
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          `Show your ${orders} ${label} ${orders === 1 ? 'order' : 'orders'} at ` +
          `${prices} ${prices === 1 ? 'price' : 'prices'} not drawn in the displayed book`
        }
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        className={cn(
          'text-muted-foreground hover:text-foreground truncate rounded text-[10px] font-normal normal-case tracking-normal transition-colors',
          // The header form shares one line with the column label, so it
          // says the counts and the caret only (the accessible name and the
          // panel heading carry "not drawn"); the row form has the width.
          inline
            ? 'ml-2 inline-block max-w-[calc(100%-6.5rem)] align-baseline leading-[15px]'
            : 'block h-4 w-full leading-4',
          position === 'left' ? 'text-right' : 'text-left',
          'focus-visible:ring-primary/70 focus-visible:outline-none focus-visible:ring-2',
          open && 'bg-muted/60 text-foreground',
        )}
      >
        {narrow
          ? `+${orders} ${orders === 1 ? 'order' : 'orders'}${inline ? '' : ' not shown'} ▾`
          : `+${summary}${inline ? ' ▾' : ' not shown ▾'}`}
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-label={`Your ${label} orders not drawn in the displayed book`}
            tabIndex={-1}
            data-ladder-overflow-panel=""
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              top: pos.top,
              left: pos.left,
              width: OVERFLOW_PANEL_WIDTH,
              maxHeight: OVERFLOW_PANEL_MAX_HEIGHT,
            }}
            className={cn(
              'bg-card border-border fixed z-[100] overflow-y-auto rounded-lg border',
              'flex flex-col gap-0.5 p-1.5 shadow-lg',
              'text-[10px] font-normal leading-4 focus:outline-none',
            )}
          >
            <div className="text-muted-foreground mb-1 whitespace-nowrap px-0.5 text-[9px] font-semibold uppercase tracking-wider">
              {label} · {summary}
            </div>
            <ul className="m-0 list-none p-0">
              {overflow.map((o) => (
                <li
                  key={o.priceCents}
                  data-overflow-price={o.priceCents}
                  data-overflow-placement={o.placement}
                  className="grid grid-cols-[3rem_minmax(0,1fr)_4rem_5rem] gap-x-1.5 whitespace-nowrap px-0.5 font-mono tabular-nums"
                >
                  <span data-overflow-summary="" className="contents">
                    <span className={tone}>{o.priceCents}¢</span>
                    <span className="text-right">{Math.round(o.ownSize)} resting</span>
                    <span className="text-muted-foreground text-right">
                      {o.orderCount} {o.orderCount === 1 ? 'order' : 'orders'}
                    </span>
                    <span className="text-muted-foreground/70 text-right">
                      {PLACEMENT_LABEL[o.placement]}
                    </span>
                  </span>
                  {/* #884 — per order: remaining and the queue ahead, the two
                      facts its chip would show; "last confirmed" marks a
                      remembered reading (#666). */}
                  <span
                    data-overflow-orders=""
                    className="text-muted-foreground/80 col-span-4 whitespace-normal pl-1 text-[9px] leading-[14px]"
                  >
                    {o.orders
                      .map(
                        (ord) =>
                          `${ord.remainingCount} left` +
                          (ord.queueOrBetter != null
                            ? ` · ${ord.queueOrBetter} ahead${ord.queueRetained ? ' (last confirmed)' : ''}`
                            : ' · queue unknown'),
                      )
                      .join('  |  ')}
                  </span>
                </li>
              ))}
            </ul>
            <div className="text-muted-foreground/70 mt-1 px-0.5 text-[9px]">
              Read-only: nothing here places or cancels.
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/** The bid block's disclosure host: one rung-height row appended BELOW the
 *  inline own rows, so it never moves a market row above it. */
function CompactOverflowRow({
  mark,
  ...props
}: Omit<Parameters<typeof OverflowDisclosure>[0], 'inline'> & { mark?: Lt2CellMark }) {
  return (
    <tr data-ladder-overflow="" className={COMPACT_ROW} onClick={(e) => e.stopPropagation()}>
      <td colSpan={3} className="max-w-0 border-y border-transparent px-1.5 py-0 align-middle" {...mark}>
        <OverflowDisclosure {...props} />
      </td>
    </tr>
  );
}

/** The hotkey fence's marks on the one element showing a key's price (see
 *  `lt2PriceMarks`). `data-lt2-exact` tells the fence this is the price text
 *  itself, which must be WHOLLY on screen, not a row passing at half. */
type Lt2CellMark = { 'data-lt2-focused': string; 'data-lt2-price': 'a' | 'd'; 'data-lt2-exact': '' };

/** The ladder's inner two-column body — a plain div so the multi-line panel
 *  (LT2-017) can lay three of them side by side inside one table row. */
export function TwoSidedLadderBody({
  kalshi,
  venue = 'kalshi',
  reference,
  yesLabel,
  noLabel,
  userOrders,
  availability,
  makerRebatePct,
  onLevelClick,
  flipColumns = false,
  presentation = DEFAULT_LADDER_PRESENTATION,
  lt2PriceMarks = null,
}: Omit<TwoSidedLadderProps, 'colSpan' | 'focused' | 'lt2FocusId'> & {
  /**
   * #953 — the focused card's id when THIS body shows the price A/D post
   * (the nearest-lines panel's selected block), else null. Each column then
   * marks the first row of its bid block — the best displayed bid, which is
   * the join `lt2TargetsFor` reads — `"a"` on the LEFT column and `"d"` on
   * the right, so each key proves its own price is on screen rather than the
   * whole panel row. The mark sits on the price TEXT, not its cell, and is
   * EXACT: the fence needs the digits wholly visible, since the cell can be
   * mostly on screen with only its odds showing (#959 review). A column with
   * no market bid marks whatever holds that place (an own-only row's price,
   * the disclosure cell or the "no bids" text), so the press still reaches
   * the fence's "No displayed price" refusal instead of reading as
   * off-screen. Compact/narrow layouts only; the single `TwoSidedLadder`
   * keeps its whole-row mark and never passes this.
   */
  lt2PriceMarks?: string | null;
}) {
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const hideUnknownFeePrices = useDisplaySettings((s) => s.hideUnknownFeePrices);
  const ticker = kalshi?.marketTicker ?? null;
  const feeEntry = useFeeFor(ticker);
  const resolvedFeeStatus = useFeeDisplayStatus(ticker, feeEntry);
  const feeStatus = feeAdjusted && venue === 'kalshi' ? resolvedFeeStatus : 'not-applicable';
  const hidePrices = hideUnknownFeePrices && feeDisplayCanHide(feeStatus);
  const active = availability === 'active';
  const { depth, layout } = presentation;
  // Narrow is the compact geometry with order-count cells (no inline chips)
  // and smaller rung text; everything below that branches on `compact`
  // applies to both, including the level cancel that leads each own-order
  // cell (#1190).
  const compact = layout !== 'default';
  const narrow = layout === 'narrow';
  const rungText = 'text-xs';

  const columnFor = (side: 'yes' | 'no', label: string): ColumnData => {
    const displayed = buildSideLevels(kalshi, side, depth);
    const ownOrders = (userOrders ?? []).filter((o) => o.side === side);
    const ownBuys = ownOrders.filter((o) => o.action === 'buy');
    const ownSells = ownOrders.filter((o) => o.action === 'sell');
    if (!compact) {
      // The original ladder, DOM for DOM: buys inject on the bid block, asks
      // are the displayed levels worst-first, nothing else classified.
      const bids = withOwnLevels(displayed.bids, ownBuys, { kind: 'bid' });
      return {
        side,
        label,
        askRows: [...displayed.asks]
          .reverse()
          .map((level) => ({ level, injected: false, beyondDepth: false })),
        bidRows: bids.rows,
        askOverflow: [],
        bidOverflow: [],
        spreadRow: null,
        spreadCents: null,
        ownBuys,
        ownSells,
      };
    }
    // Compact: the whole sanity-filtered side is resolved once per column and
    // only if some own order is missing from the displayed levels.
    let full: ReturnType<typeof buildSideLevels> | null = null;
    const resolveFull = () => (full ??= buildSideLevels(kalshi, side, Number.POSITIVE_INFINITY));
    // Ask block: never an own-only row inline (rows above the asks would push
    // the whole book down under the pointer); every own ask extra is listed
    // from the column header. Bid block: the market rows, then a separate
    // own block of up to MAX_EXTRA_OWN_ROWS own-only rows (feed-gap and
    // beyond-depth alike, never interleaved), then the disclosure row.
    const asks = withOwnLevels(displayed.asks, ownSells, {
      kind: 'ask',
      resolveFull: () => resolveFull().asks,
      spreadSlot: true,
      inlineExtras: false,
      book: kalshi,
    });
    const bids = withOwnLevels(displayed.bids, ownBuys, {
      kind: 'bid',
      maxExtra: MAX_EXTRA_OWN_ROWS,
      resolveFull: () => resolveFull().bids,
      spreadSlot: true,
      separateOwnBlock: true,
      book: kalshi,
    });
    // The spread rung is exactly one row: an ask inside the spread first
    // (it sits nearer the asks), else a bid, best first. Every other
    // inside-spread order is listed by its own block's disclosure.
    const inside = [
      ...asks.spread.map((e) => ({ e, kind: 'ask' as const })),
      ...bids.spread.map((e) => ({ e, kind: 'bid' as const })),
    ];
    const [first, ...restInside] = inside;
    const spreadRow = first
      ? {
          row: { level: first.e.level, injected: first.e.injected, beyondDepth: first.e.beyondDepth },
          kind: first.kind,
        }
      : null;
    const byPriceDesc = (a: OwnOverflow, b: OwnOverflow) => b.priceCents - a.priceCents;
    const askOverflow = [
      ...asks.overflow,
      ...restInside
        .filter((c) => c.kind === 'ask')
        .map((c) => ownExtraToOverflow(c.e, 'inside-spread', kalshi)),
    ].sort(byPriceDesc);
    const bidOverflow = [
      ...bids.overflow,
      ...restInside
        .filter((c) => c.kind === 'bid')
        .map((c) => ownExtraToOverflow(c.e, 'inside-spread', kalshi)),
    ].sort(byPriceDesc);
    const bestAsk = displayed.asks[0]?.priceCents ?? null;
    const bestBid = displayed.bids[0]?.priceCents ?? null;
    return {
      side,
      label,
      askRows: asks.rows,
      bidRows: bids.rows,
      askOverflow,
      bidOverflow,
      spreadRow,
      spreadCents:
        bestAsk != null && bestBid != null
          ? fromCentiCents(toCentiCents(bestAsk) - toCentiCents(bestBid))
          : null,
      ownBuys,
      ownSells,
    };
  };

  const chipsFor = (col: ColumnData, cents: number, action: 'buy' | 'sell'): BidRowOrder[] => {
    const source = action === 'buy' ? col.ownBuys : col.ownSells;
    return source
      .filter((o) => o.priceCents === cents)
      .sort((a, b) => b.remainingCount - a.remainingCount)
      .map((o) => ({
        orderId: o.orderId,
        remainingCount: o.remainingCount,
        queueOrBetter: kalshi ? queueDepth(o, kalshi).atLevelOrBetter : null,
        tooltip: kalshi ? orderTooltip(o, kalshi) : '',
        // #676 — confirmed vs remembered decides which chips stay inline.
        queueRetained: o.queueRetained,
      }));
  };

  /** One description per rung, shared by both layouts so they cannot disagree
   *  about a price, an odds figure, a title or what a click dispatches. */
  const describeLevel = (col: ColumnData, row: LevelRow, kind: 'bid' | 'ask'): LevelPresentation => {
    const cents = row.level.priceCents;
    const price = ladderRowPricePresentation(cents, kind, feeAdjusted && venue === 'kalshi', feeEntry, makerRebatePct);
    const odds = hidePrices ? null : americanOdds(price.effectiveCents);
    const chips = chipsFor(col, cents, kind === 'bid' ? 'buy' : 'sell');
    const clickable = active && !hidePrices && !row.injected;
    const clickTitle = `Click to ${kind === 'bid' ? 'join (maker)' : 'take'} ${col.label} @ ${cents}¢`;
    const title = row.injected
      ? INJECTED_LEVEL_TITLE
      : hidePrices
        ? FEE_DISPLAY_MESSAGE[feeStatus] ?? undefined
        : !active
          ? INACTIVE_LEVEL_TITLE
          : row.beyondDepth
            ? `Your order — resting at ${cents}¢, beyond the ${depth} displayed levels (market size shown). ${clickTitle}`
            : clickTitle;
    return {
      cents,
      priceText: hidePrices ? '—' : `${price.rawCents}¢`,
      odds,
      priceTone: kind === 'ask' ? 'text-negative' : 'text-positive',
      chips,
      clickable,
      title,
      sizeTitle: row.injected ? INJECTED_SIZE_TITLE : RESTING_SIZE_TITLE,
      onClick: clickable
        ? (e) => {
            e.stopPropagation();
            onLevelClick(col.side, kind, cents);
          }
        : undefined,
    };
  };

  const yes = columnFor('yes', yesLabel);
  const no = columnFor('no', noLabel);

  // ── default layout: the original half-width columns ──────────────────
  const renderDefaultLevel = (col: ColumnData, row: LevelRow, kind: 'bid' | 'ask') => {
    const d = describeLevel(col, row, kind);
    return (
      <div
        key={`${kind}-${d.cents}`}
        className={cn(
          'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 rounded px-1.5 py-0.5',
          d.clickable && 'cursor-pointer hover:bg-muted/40',
          row.injected && 'border border-dashed border-primary/50',
        )}
        onClick={d.onClick}
        title={d.title}
      >
        <span className="flex items-center gap-1 whitespace-nowrap font-mono text-xs">
          <span className={d.priceTone}>{d.priceText}</span>
          {d.odds && (
            <>
              <span className="text-muted-foreground/40">|</span>
              <span className="text-foreground">{d.odds}</span>
            </>
          )}
          <FeeScheduleMarker status={feeStatus} />
        </span>
        <span className="text-muted-foreground w-[6ch] text-right font-mono text-xs" title={d.sizeTitle}>
          {formatVolume(row.level.size)}
        </span>
        <span
          className="flex min-w-[3ch] items-center justify-end gap-1 text-[10px] leading-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* #650 — bounded, same component as the two table ladders.
              `level={null}`: #523 named three surfaces — the dashboard
              moneyline ladder, the Spreads/Totals ladder and the compact
              Game Detail moneyline bands — and this default layout is not
              among them, so it has never offered the level-wide cancel.
              (The narrow and compact layouts below do: the Butterfly fix
              b4ab04577 and #1190.) Quietly adding a destructive control
              here is not this ticket's to make. */}
          <RestingOrderGroup orders={d.chips} level={null} priceCents={d.cents} />
        </span>
      </div>
    );
  };

  const renderDefaultColumn = (col: ColumnData) => (
    <div className="min-w-0 flex-1">
      <div
        className={cn(
          'mb-1 border-b border-border/40 pb-0.5 text-[10px] font-semibold uppercase tracking-wider',
          col.side === 'yes' ? 'text-positive' : 'text-negative',
        )}
      >
        {col.label}
        <span className="text-muted-foreground/60 ml-2 normal-case tracking-normal">
          asks / bids
        </span>
      </div>
      <div className="flex flex-col gap-px">
        {col.askRows.length === 0 && (
          <div className="text-muted-foreground/50 px-1.5 py-0.5 text-[10px]">no asks</div>
        )}
        {col.askRows.map((row) => renderDefaultLevel(col, row, 'ask'))}
        <div className="border-border/40 my-0.5 border-t" aria-hidden />
        {col.bidRows.length === 0 && (
          <div className="text-muted-foreground/50 px-1.5 py-0.5 text-[10px]">no bids</div>
        )}
        {col.bidRows.map((row) => renderDefaultLevel(col, row, 'bid'))}
      </div>
    </div>
  );

  if (!compact) {
    return (
      <div
        className={cn(
          'flex gap-6 px-2',
          !active && 'opacity-60',
          // LT2-018 display-order flip: the NO column renders LEFT. Sides,
          // labels and clicks are untouched.
          flipColumns && 'flex-row-reverse',
        )}
        data-ladder-layout="default"
      >
        {renderDefaultColumn(yes)}
        {renderDefaultColumn(no)}
      </div>
    );
  }

  // ── compact layout (#882): packed to the shared center ───────────────
  //
  // Each column is one small table so its three cells align across rungs
  // without a stretched price column. The column on the LEFT of the center
  // reads own-chips | price | resting; the column on the RIGHT reads
  // price | resting | own-chips — prices and resting sizes meet at the center
  // and the reserved own-order cell sits on the outside, so an order
  // arriving or leaving moves nothing the trader is aiming at. Blocks pad to
  // the larger of `depth` and the other column's count, so the two spread
  // rungs stay level. Own-order state never moves a market row: the ask
  // block draws no own-only row (its extras are listed from the permanent
  // header), the spread rung is exactly one row and holds one inside-spread
  // order (the rest are listed), and the bid block's own-only rows —
  // feed-gap and beyond-depth alike — form a separate block AFTER the
  // market bids (`data-ladder-own-block`), followed by the disclosure row.
  // The ladder can therefore grow by at most MAX_EXTRA_OWN_ROWS + 1 rows
  // at its bottom edge, moving what sits under the ladder, never a price
  // inside it. A feed level arriving or leaving still moves rows, as any
  // levels ladder does.
  // The ask block holds market rows only (its own extras live in the header),
  // so it is exactly `depth` rows whenever either column has that many.
  const askBlock = Math.max(depth, yes.askRows.length, no.askRows.length);
  const bidBlock = Math.max(
    depth,
    blockRows(yes.bidRows, yes.bidOverflow),
    blockRows(no.bidRows, no.bidOverflow),
  );
  const left = flipColumns ? no : yes;
  const right = flipColumns ? yes : no;

  const renderCompactLevel = (
    col: ColumnData,
    row: LevelRow,
    kind: 'bid' | 'ask',
    position: 'left' | 'right',
    /** Set for rows of the bid block's separate own block; `start` marks
     *  its first row, which draws the hairline that separates it from the
     *  market bids. */
    ownBlock?: { start: boolean },
    mark?: Lt2CellMark,
  ) => {
    const d = describeLevel(col, row, kind);
    // Every rung cell carries a transparent top and bottom border, so a
    // marked row (injected: dashed; own-block start: solid top) recolors
    // borders that are already in the row's height instead of adding 1–2px
    // and moving every price below it. Rows are pinned to COMPACT_ROW.
    const edges = cn(
      'border-y border-transparent',
      row.injected && 'border-dashed border-primary/50',
      ownBlock?.start && 'border-t-border/70',
    );
    const priceCell = (
      <td key="price" className={cn('whitespace-nowrap px-1.5 py-0 text-left align-middle font-mono', rungText, edges)}>
        <span className="inline-flex items-center gap-1 leading-4">
          <span className={d.priceTone} {...mark}>{d.priceText}</span>
          {d.odds && (
            <>
              {!narrow && <span className="text-muted-foreground/40">|</span>}
              <span className="text-foreground">{d.odds}</span>
            </>
          )}
          <FeeScheduleMarker status={feeStatus} />
        </span>
      </td>
    );
    const sizeCell = (
      <td
        key="size"
        className={cn(
          'text-muted-foreground w-[6ch] whitespace-nowrap px-1.5 py-0 text-right align-middle font-mono leading-4 tabular-nums',
          rungText,
          edges,
        )}
        title={d.sizeTitle}
      >
        {formatVolume(row.level.size)}
      </td>
    );
    const chipsCell = (
      <td
        key="chips"
        // Reserved width: the bounded chip group's widest form (#650 compact
        // `+N`, #677 expanded max-width) plus the level cancel (#1190). Fixed
        // so an own order arriving never moves the price beside it.
        className={cn(
          narrow ? NARROW_OWN_CELL : COMPACT_OWN_CELL,
          'px-1 py-0 align-middle text-[10px] leading-4',
          position === 'left' ? 'text-right' : 'text-left',
          edges,
          row.injected && (position === 'left' ? 'border-l' : 'border-r'),
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className={cn(
            'flex items-center', narrow ? 'gap-0' : 'gap-1',
            position === 'left' ? 'justify-end' : 'justify-start',
            // #1190 — on the left column the group packs toward the price, so
            // the level cancel leading it would slide as chips come and go.
            // Pin it to the cell's outer edge instead, as the moneyline gutter
            // does: a destructive control must not move under the pointer.
            !narrow && position === 'left' && '[&>button:first-child]:mr-auto',
          )}
        >
          {/* #1190 — both layouts lead with the existing frozen-set level
              cancel (#523), in its overlay form so arming never widens this
              reserved cell. The narrow layout keeps individual orders in the
              count disclosure; the compact one draws them as chips. */}
          {ticker && d.chips.length > 0 && (
            <CancelLevelControl
              compact
              level={{ marketTicker: ticker, side: col.side, action: kind === 'bid' ? 'buy' : 'sell', priceCents: d.cents }}
              orders={d.chips}
            />
          )}
          <RestingOrderGroup
            orders={d.chips}
            level={null}
            priceCents={d.cents}
            inlineLimit={narrow ? 0 : undefined}
            display={narrow ? 'count' : undefined}
          />
        </span>
      </td>
    );
    const cells: ReactNode[] =
      position === 'left' ? [chipsCell, priceCell, sizeCell] : [priceCell, sizeCell, chipsCell];
    return (
      <tr
        key={`${kind}-${d.cents}`}
        className={cn(
          COMPACT_ROW,
          d.clickable && 'cursor-pointer hover:bg-muted/40',
          (row.beyondDepth || ownBlock) && 'bg-muted/30',
        )}
        onClick={d.onClick}
        title={d.title}
        data-ladder-rung={`${kind}-${d.cents}`}
        data-injected={row.injected ? '' : undefined}
        data-beyond-depth={row.beyondDepth ? '' : undefined}
        data-ladder-own-block={ownBlock ? '' : undefined}
        data-ladder-own-block-start={ownBlock?.start ? '' : undefined}
      >
        {cells}
      </tr>
    );
  };

  const renderCompactColumn = (col: ColumnData, position: 'left' | 'right') => {
    const askPad = askBlock - col.askRows.length;
    const bidPad = bidBlock - blockRows(col.bidRows, col.bidOverflow);
    const firstOwnBid = col.bidRows.findIndex((row) => row.injected || row.beyondDepth);
    // #953 — see `lt2PriceMarks`. Rows render bids → disclosure → pads, so
    // exactly one of the three branches below receives this mark.
    const mark: Lt2CellMark | undefined =
      lt2PriceMarks == null
        ? undefined
        : {
            'data-lt2-focused': lt2PriceMarks,
            'data-lt2-price': position === 'left' ? 'a' : 'd',
            'data-lt2-exact': '',
          };
    const captions =
      position === 'left' ? ['yours', 'price', 'resting'] : ['price', 'resting', 'yours'];
    const captionAlign = (caption: string) =>
      caption === 'resting'
        ? 'text-right'
        : caption === 'yours'
          ? position === 'left'
            ? 'text-right'
            : 'text-left'
          : 'text-left';
    return (
      <table
        key={col.side}
        data-testid={`ladder-column-${col.side}`}
        data-ladder-position={position}
        className={cn("shrink-0 border-separate border-spacing-0", narrow && "[&_td]:px-1 [&_th]:px-1")}
      >
        <thead>
          <tr>
            <th
              colSpan={3}
              className={cn(
                // Fixed header heights: the column with the inline disclosure
                // must not be a pixel taller than the one without it.
                'h-5 border-b border-border/40 py-0 align-bottom text-[10px] font-semibold uppercase leading-4 tracking-wider',
                position === 'left' ? 'text-right' : 'text-left',
                col.side === 'yes' ? 'text-positive' : 'text-negative',
              )}
            >
              {col.label}
              <span className="text-muted-foreground/60 ml-2 normal-case tracking-normal">
                asks / bids
              </span>
              {/* Own asks that are not drawn are listed from here: the header
                  is a permanent row, so the ask block never grows upward into
                  the trader's aim. */}
              {col.askOverflow.length > 0 && (
                <span data-ladder-overflow-header="" onClick={(e) => e.stopPropagation()}>
                  <OverflowDisclosure
                    overflow={col.askOverflow}
                    label={`${col.label} sell`}
                    kind="ask"
                    position={position}
                    inline
                    narrow={narrow}
                  />
                </span>
              )}
            </th>
          </tr>
          <tr className="text-muted-foreground/70 text-[9px] font-semibold uppercase tracking-wide">
            {captions.map((caption) => (
              <th
                key={caption}
                scope="col"
                className={cn('h-5 px-1.5 py-0 align-bottom font-semibold leading-4', captionAlign(caption))}
              >
                {caption}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: askPad }, (_, i) => (
            <CompactBlankRung
              key={`ask-pad-${i}`}
              // The blank nearest the separator carries the empty state, so
              // "no asks" is a visible fact rather than five silent rows.
              state={col.askRows.length === 0 && i === askPad - 1 ? 'no asks' : null}
            />
          ))}
          {col.askRows.map((row) => renderCompactLevel(col, row, 'ask', position))}
          {/* Exactly one spread rung: the column's one inside-spread own
              order, else the spread itself. */}
          {col.spreadRow ? (
            renderCompactLevel(col, col.spreadRow.row, col.spreadRow.kind, position)
          ) : (
            <CompactSpreadRung
              text={col.spreadCents == null ? 'spread —' : `${col.spreadCents}¢ spread`}
              position={position}
            />
          )}
          {col.bidRows.map((row, i) =>
            renderCompactLevel(
              col,
              row,
              'bid',
              position,
              // By construction (`separateOwnBlock`) every own-only bid row
              // follows every market row, so the first one starts the block.
              row.injected || row.beyondDepth ? { start: i === firstOwnBid } : undefined,
              i === 0 ? mark : undefined,
            ),
          )}
          {col.bidOverflow.length > 0 && (
            <CompactOverflowRow
              overflow={col.bidOverflow}
              label={`${col.label} buy`}
              kind="bid"
              position={position}
              narrow={narrow}
              mark={col.bidRows.length === 0 ? mark : undefined}
            />
          )}
          {Array.from({ length: bidPad }, (_, i) => (
            <CompactBlankRung
              key={`bid-pad-${i}`}
              state={col.bidRows.length === 0 && i === 0 ? 'no bids' : null}
              mark={
                col.bidRows.length === 0 && col.bidOverflow.length === 0 && i === 0
                  ? mark
                  : undefined
              }
            />
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div
      className={cn(
        // `items-start`: a column with more own-order rows must not make the
        // other column's table stretch — a stretched table spreads the extra
        // height across its rows and moves every price in it by a fraction.
        'flex items-start justify-center',
        narrow ? 'gap-0.5 px-0' : 'gap-6 px-2',
        !active && 'opacity-60',
      )}
      data-ladder-layout={layout}
      data-ladder-ticker={ticker}
      data-ladder-venue={venue}
      data-ladder-depth={depth}
    >
      {renderCompactColumn(left, 'left')}
      {reference && <ReferenceRail reference={reference} book={kalshi} columns={[left, right].map(col => ({
        side: col.side,
        asks: [...Array.from({ length: Math.max(0, depth - col.askRows.length) }, () => null), ...col.askRows.map(r => r.level), null],
        // Own-only bids are a separate block after the displayed market rows.
        // They must not extend the reference range or move its clamp point.
        bids: col.bidRows.filter(r => !r.injected && !r.beyondDepth).map(r => r.level),
      }))} />}
      {renderCompactColumn(right, 'right')}
    </div>
  );
}

export function TwoSidedLadder({
  colSpan = 12,
  focused = false,
  lt2FocusId = null,
  ...body
}: TwoSidedLadderProps) {
  const marks = focused && lt2FocusId ? lt2FocusId : undefined;
  return (
    <tr
      className={cn('border-0', focused && '!bg-primary/15')}
      data-testid="two-sided-ladder"
      data-lt2-focused={marks}
      data-lt2-price={marks == null ? undefined : ''}
      data-lt2-hotkey-control={marks == null ? undefined : ''}
    >
      <Td colSpan={colSpan} className="py-1.5">
        <div className="mb-1 text-[9px] text-amber-400">
          {body.kalshi?.bookSourceValidity === 'unconfirmed'
            ? (body.kalshi.retainedDepth ? 'Last confirmed book' : 'Awaiting book')
            : <OrderbookFreshness ticker={body.kalshi?.marketTicker} />}
        </div>
        <TwoSidedLadderBody {...body} />
      </Td>
    </tr>
  );
}
